import { useRef, useMemo } from 'react';
import styled from 'styled-components';
import { s } from '../components/PeriodStatsPage.styles';
import { downloadMultiSheetExcel } from '../../../utils/downloadAsExcel';
import type { TrendRow } from './graph/AuctionTrendAnalysisGraph';
import SaveAsImageButton from '../../components/button/SaveAsImageButton';
import StackedCountChart from './graph/StackedCountChart';
import SimpleLineChart from './graph/SimpleLineChart';

const LEFT_W = 50; // 좌측 축 폭

type AnnualNationalAuctionTrendProps = {
  region: string;
  usage: string;
  usageDetail?: string;
  court?: string;
  type: 'week' | 'month' | 'year';
  data: TrendRow[];
  loading?: boolean;
  error?: string | null;
};

export default function PeriodAuctionTrend({
  region,
  usage,
  usageDetail,
  court,
  type,
  data,
  loading = false,
  error = null,
}: AnnualNationalAuctionTrendProps) {
  const targetRef = useRef<HTMLDivElement>(null);

  const periodLabel = useMemo(() => {
    switch (type) {
      case 'week':
        return '주별';
      case 'month':
        return '월별';
      case 'year':
      default:
        return '연별';
    }
  }, [type]);

  const isWeek = type === 'week';

  const periodFileName = useMemo(() => {
    switch (type) {
      case 'week':
        return '3개월_전국_경매_지표_동향';
      case 'month':
        return '월간_전국_경매_지표_동향';
      case 'year':
      default:
        return '연간_전국_경매_지표_동향';
    }
  }, [type]);

  const EXCEL_SHEETS = useMemo(() => {
    const base = data ?? [];
    const sheet1 = base.map((d) => ({
      기간: d.label,
      진행건수: d.progress,
      낙찰건수: d.wonCount,
    }));
    const sheet2 = base.map((d) => ({
      기간: d.label,
      '낙찰률(%)': Number(d.winRate.toFixed(1)),
    }));
    const sheet3 = base.map((d) => ({
      기간: d.label,
      '낙찰가율(%)': Number(d.winPriceRate.toFixed(1)),
    }));
    const sheet4 = base.map((d) => ({
      기간: d.label,
      '평균 응찰자 수(명)': Number(d.avgBidders.toFixed(1)),
    }));
    return { sheet1, sheet2, sheet3, sheet4 };
  }, [data]);

  const handleDownloadExcel = () => {
    downloadMultiSheetExcel(
      {
        [`${periodLabel} 진행건수 및 낙찰 건수`]: EXCEL_SHEETS.sheet1,
        [`${periodLabel} 낙찰률`]: EXCEL_SHEETS.sheet2,
        [`${periodLabel} 낙찰가율`]: EXCEL_SHEETS.sheet3,
        [`${periodLabel} 평균 응찰자 수`]: EXCEL_SHEETS.sheet4,
      },
      periodFileName,
    );
  };

  const toNum = (v: any) =>
    typeof v === 'string' ? Number(v.replaceAll(',', '')) : Number(v) || 0;

  // 진행건수/낙찰건수 시각화를 위한 렌더링용 데이터
  const chartData = useMemo(() => {
    return (data ?? []).map((d) => {
      const p = toNum(d.progress);
      const w = Math.max(0, toNum(d.wonCount));
      const wonCountCapped = Math.min(w, p);
      const progressRemainder = Math.max(0, p - wonCountCapped);
      return { ...d, wonCountCapped, progressRemainder };
    });
  }, [data]);

  // 최댓값의 MSD 올림 규칙
  const axisMaxByMSDRule = (n: number) => {
    if (!isFinite(n) || n <= 0) return 1;
    const intPart = Math.floor(n);
    if (intPart === 0) return 1;
    const digits = Math.floor(Math.log10(intPart)) + 1;
    const msd = Math.floor(intPart / Math.pow(10, digits - 1));
    return (msd + 1) * Math.pow(10, digits - 1);
  };

  return (
    <s.Wrap ref={targetRef}>
      <s.Header>
        <s.HeaderRow>
          <s.Img $bg="#feefef">
            <img src="/assets/images/icon/red_calendar.svg" alt="" />
          </s.Img>
          <s.ButtonDiv>
            <SaveAsImageButton
              targetRef={targetRef}
              fileName={`${periodFileName}.png`}
            />
            <s.ExcelButton onClick={handleDownloadExcel} aria-label="엑셀 저장">
              <img src="/assets/images/icon/download-black.svg" alt="" />
            </s.ExcelButton>
          </s.ButtonDiv>
        </s.HeaderRow>
        <s.TitleBox>
          {isWeek ? (
            <>
              <h3>3개월 전국 경매 지표 동향</h3>
              <p>
                최근 3개월 주거시설, 업무·상업시설, 토지, 공업시설의 주요 경매
                지표 동향
              </p>
            </>
          ) : (
            <>
              <h3>연간 전국 경매 지표 동향</h3>
              <p>
                올해 주거시설, 업무·상업시설, 토지, 공업시설의 주요 경매 지표
                동향
              </p>
            </>
          )}

          <s.SelectedOptionBox>
            <s.SelectedOption>
              {court && court !== 'all' ? court : `${region} 지역`}
            </s.SelectedOption>
            <s.SelectedOption>
              {usage === '전체'
                ? `${usage} 용도`
                : `${usage} ${usageDetail ? ` ${usageDetail} 용도` : ''}`}
            </s.SelectedOption>
          </s.SelectedOptionBox>
        </s.TitleBox>
      </s.Header>

      {loading ? (
        <LoaderWrap>
          <LoaderImg
            src="/assets/images/icon/loader.gif"
            alt="전국 경매 지표"
          />
        </LoaderWrap>
      ) : (
        <Container>
          {/* 진행건수/낙찰건수 */}
          <Section>
            <GraphTitle>{periodLabel} 진행건수 및 낙찰 건수</GraphTitle>
            <GraphContainer>
              <StackedCountChart
                data={data}
                height={320}
                tickWidth={80}
                leftAxisWidth={50}
              />
              <LabelRow>
                <GraphLabel>
                  <LabelMarker $color="#D3D8E0" />
                  진행건수
                </GraphLabel>
                <GraphLabel>
                  <LabelMarker $color="#424854" />
                  낙찰건수
                </GraphLabel>
              </LabelRow>
            </GraphContainer>
          </Section>

          {/* 낙찰률 */}
          <Section>
            <GraphTitle>{periodLabel} 낙찰률</GraphTitle>
            <GraphContainer>
              <SimpleLineChart
                data={data}
                dataKey="winRate"
                color="#0F9667"
                label="낙찰률"
                yLabel="비율"
              />
              <DotProgress>
                <ProgressBar />
                <Dot />
                <ProgressBar />
                <LabelTag>낙찰률</LabelTag>
              </DotProgress>
            </GraphContainer>
          </Section>

          {/* 낙찰가율 */}
          <Section>
            <GraphTitle>{periodLabel} 낙찰가율</GraphTitle>
            <GraphContainer>
              <SimpleLineChart
                data={data}
                dataKey="winPriceRate"
                color="#3F6EF1"
                label="낙찰가율"
                yLabel="비율"
              />
              <DotProgress $width="92px">
                <ProgressBar $color="#3F6EF1" />
                <Dot $color="#3F6EF1" />
                <ProgressBar $color="#3F6EF1" />
                <LabelTag>낙찰가율</LabelTag>
              </DotProgress>
            </GraphContainer>
          </Section>

          {/* 평균 응찰자 수 */}
          <Section>
            <GraphTitle>{periodLabel} 평균 응찰자 수</GraphTitle>
            <GraphContainer>
              <SimpleLineChart
                data={data}
                dataKey="avgBidders"
                color="#F15171"
                label="평균 응찰자 수"
                yLabel="인원 수"
              />

              <DotProgress $width="120px">
                <ProgressBar $color="#F15171" />
                <Dot $color="#F15171" />
                <ProgressBar $color="#F15171" />
                <LabelTag>평균 응찰자 수</LabelTag>
              </DotProgress>
            </GraphContainer>
          </Section>
        </Container>
      )}
    </s.Wrap>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const GraphContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const GraphTitle = styled.div`
  color: #0c0e12;
  font-size: 18px;
  font-weight: 500;
  line-height: 28px;
  letter-spacing: -0.36px;
`;

const LabelRow = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  align-self: stretch;
`;

const GraphLabel = styled.div`
  display: flex;
  padding: 8px;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 500;
  color: #424854;
`;

const LabelMarker = styled.div<{ $color: string }>`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  margin: 4px;
  background-color: ${({ $color }) => $color};
`;

const DotProgress = styled.div<{ $width?: string }>`
  align-self: center;
  display: flex;
  flex-direction: row;
  gap: 2px;
  width: ${({ $width }) => ($width ? $width : '80px')};
  align-items: center;
  margin-top: 8px;
`;

const Dot = styled.div<{ $color?: string }>`
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  background-color: ${({ $color }) => ($color ? $color : '#0f9667')};
`;

const ProgressBar = styled.div<{ $color?: string }>`
  height: 2px;
  flex: 1;
  background-color: ${({ $color }) => ($color ? $color : '#0f9667')};
`;

const LabelTag = styled.div`
  font-size: 13px;
  color: #424854;
  margin-left: 4px;
  font-weight: 500;
`;

const LoaderWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 280px;
  width: 100%;
`;

const LoaderImg = styled.img`
  width: 24px;
  height: 24px;
`;
